import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref } from 'vue'
import { util } from '@/Helpers'

const STORAGE_KEY = 'recent_profiles'
const MAX_ITEMS = 250

function loadFromStorage() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return []
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

function saveToStorage(items) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
    } catch {
        // Storage full or unavailable — silently ignore
    }
}

function buildKey(id, type) {
    const types = Array.isArray(type) ? type : [type]
    return `${types[0]}:${id}`
}

function serializeQuickSearchResult(result) {
    const type = Array.isArray(result.type) ? result.type : [result.type]
    return {
        key: buildKey(result.id, type),
        id: result.id,
        type,
        name: result.name || '',
        subtitle: result.subtitle || null,
        location: result.location || null,
        staff: result.staff || null,
        has_alert: result.has_alert ?? result.hard_no_status ?? false,
        viewed_at: Date.now(),
    }
}

function serializeClient(client) {
    const address = client.address || {}
    const location = [address.city, address.state].filter(Boolean).join(', ')
    const staffName = client.assigned_sales_manager
        ? util.titleCase(client.assigned_sales_manager.first_name || '') + ' ' + util.titleCase(client.assigned_sales_manager.last_name || '')
        : null

    const type = [client.type || 'client']
    return {
        key: buildKey(client.id, type),
        id: client.id,
        type,
        name: util.titleCase(client.first_name || '') + ' ' + util.titleCase(client.last_name || ''),
        subtitle: null,
        location: location || null,
        staff: staffName ? staffName.trim() : null,
        has_alert: false,
        viewed_at: Date.now(),
    }
}

export const useRecentProfilesStore = defineStore('recentProfiles', () => {
    const items = ref(loadFromStorage())

    function upsert(serialized) {
        const filtered = items.value.filter(item => (item.key || item.id) !== serialized.key)
        items.value = [serialized, ...filtered].slice(0, MAX_ITEMS)
        saveToStorage(items.value)
    }

    function addFromQuickSearch(result) {
        if (!result?.id || !result?.type) return
        upsert(serializeQuickSearchResult(result))
    }

    function addFromClient(client) {
        if (!client?.id || client.fe_generated) return
        upsert(serializeClient(client))
    }

    function getByScope(scope) {
        const normalizedScope = Array.isArray(scope) ? scope : [scope]

        if (normalizedScope.includes('all')) {
            return items.value
        }

        const scopeToTypes = {
            client: ['client'],
            prospect: ['prospect'],
            lead: ['lead'],
            caregiver: ['candidate', 'caregiver', 'applicant'],
            coi: ['coi'],
        }

        const allowedTypes = new Set(
            normalizedScope.flatMap(s => scopeToTypes[s] || [s])
        )

        return items.value.filter(item =>
            item.type.some(t => allowedTypes.has(t))
        )
    }

    function remove(key) {
        items.value = items.value.filter(item => (item.key || item.id) !== key)
        saveToStorage(items.value)
    }

    function clear() {
        items.value = []
        saveToStorage(items.value)
    }

    return { items, addFromQuickSearch, addFromClient, getByScope, remove, clear }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useRecentProfilesStore, import.meta.hot))
}
